/** Administrador de variables técnicas configurables (crear sin tocar código). */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';

const TIPOS = ['texto', 'numero', 'decimal', 'entero', 'si_no', 'seleccion', 'seleccion_multiple', 'fecha', 'medida', 'area', 'longitud', 'cantidad', 'moneda'];
const AFECTA = [['costo', 'Costo'], ['despiece', 'Despiece'], ['mano_obra', 'Mano de obra'], ['logistica', 'Logística'], ['precio', 'Precio']];
const ROLES_VIS = [['user', 'Vendedor'], ['produccion', 'Producción'], ['instalador', 'Instalador']];
const errMsg = (e, d) => e?.response?.data?.detail || d;

const vacia = () => ({
  codigo: '', nombre: '', tipo: 'texto', unidad: '', familias: [], obligatoria: false, valor_default: '',
  activa: true, orden: 0, descripcion: '', ayuda: '', opciones: '', afecta: {}, visibilidad: [], dep_var: '', dep_val: '',
});

export const VariablesAdmin = ({ familias, onClose }) => {
  const [vars, setVars] = useState([]);
  const [f, setF] = useState(null); // null = solo lista
  const [editId, setEditId] = useState(null);

  const load = () => api.get('/sa/variables').then((r) => setVars(r.data.variables || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const abrirEdicion = (v) => {
    setEditId(v.id);
    setF({
      ...vacia(), ...v, opciones: (v.opciones || []).join(', '),
      valor_default: v.valor_default ?? '', afecta: v.afecta || {}, visibilidad: v.visibilidad || [],
      dep_var: v.depende_de?.variable || '', dep_val: v.depende_de?.valor ?? '',
    });
  };

  const guardar = async () => {
    if (!f.codigo || !f.nombre) return toast.error('Código y nombre son requeridos');
    const body = {
      codigo: f.codigo.trim().toLowerCase().replace(/\s+/g, '_'), nombre: f.nombre, tipo: f.tipo,
      unidad: f.unidad, familias: f.familias, obligatoria: !!f.obligatoria,
      valor_default: f.valor_default === '' ? null : f.valor_default, activa: !!f.activa,
      orden: Number(f.orden || 0), descripcion: f.descripcion, ayuda: f.ayuda,
      opciones: f.opciones.split(',').map((s) => s.trim()).filter(Boolean),
      afecta: f.afecta, visibilidad: f.visibilidad,
      depende_de: f.dep_var ? { variable: f.dep_var, valor: f.dep_val } : null,
    };
    try {
      if (editId) await api.put(`/sa/variables/${editId}`, body);
      else await api.post('/sa/variables', body);
      toast.success(editId ? 'Variable actualizada' : 'Variable creada');
      setF(null); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); }
  };

  const borrar = async (v) => {
    if (!window.confirm(`¿Eliminar la variable ${v.codigo}?`)) return;
    try { await api.delete(`/sa/variables/${v.id}`); load(); } catch (e) { toast.error(errMsg(e, 'Error')); }
  };

  const toggleLista = (key, val) => setF({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[680px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Valores generales de cálculo</DialogTitle></DialogHeader>

        {!f ? (
          <>
            <Button size="sm" onClick={() => { setF(vacia()); setEditId(null); }} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white w-fit" data-testid="vars-nueva">
              <Plus size={14} className="mr-1" /> Nueva variable
            </Button>
            <div className="border border-[var(--border)] rounded-lg divide-y divide-[var(--border)] max-h-[55vh] overflow-y-auto">
              {vars.length === 0 ? <p className="p-4 text-sm text-[var(--text-muted)] text-center">Sin variables. Crea la primera.</p>
                : vars.map((v) => (
                  <div key={v.id} className="flex items-center justify-between px-3 py-2 text-sm gap-2" data-testid={`vars-item-${v.codigo}`}>
                    <div className="min-w-0">
                      <p className="font-bold truncate">
                        <span className="text-[#0EA5E9] mr-1.5">{v.codigo}</span>{v.nombre}
                        {v.activa === false && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">inactiva</span>}
                        {v.depende_de?.variable && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">depende de {v.depende_de.variable}={String(v.depende_de.valor)}</span>}
                      </p>
                      <p className="text-[11px] text-[var(--text-muted)] truncate">{v.tipo}{v.unidad ? ` · ${v.unidad}` : ''} · {(v.familias || []).join(', ') || 'sin familia'}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => abrirEdicion(v)} data-testid={`vars-edit-${v.codigo}`}><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-red-400" onClick={() => borrar(v)} data-testid={`vars-del-${v.codigo}`}><Trash size={14} /></Button>
                    </div>
                  </div>
                ))}
            </div>
          </>
        ) : (
          <div className="space-y-2.5" data-testid="vars-form">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Código *</label>
                <Input value={f.codigo} disabled={!!editId} onChange={(e) => setF({ ...f, codigo: e.target.value })} className="h-9" data-testid="vars-f-codigo" /></div>
              <div className="col-span-1 sm:col-span-2"><label className="text-xs font-bold text-[var(--text-muted)]">Nombre *</label>
                <Input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="h-9" data-testid="vars-f-nombre" /></div>
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Tipo</label>
                <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="w-full h-9 text-sm border border-[var(--border)] rounded-md bg-[var(--card)] text-[var(--text)] px-2" data-testid="vars-f-tipo">
                  {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Unidad</label>
                <Input value={f.unidad} onChange={(e) => setF({ ...f, unidad: e.target.value })} className="h-9" placeholder="m, ml, m², u…" /></div>
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Valor predeterminado</label>
                <Input value={f.valor_default} onChange={(e) => setF({ ...f, valor_default: e.target.value })} className="h-9" /></div>
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Orden</label>
                <Input type="number" value={f.orden} onChange={(e) => setF({ ...f, orden: e.target.value })} className="h-9" /></div>
            </div>
            {['seleccion', 'seleccion_multiple'].includes(f.tipo) && (
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Opciones (separadas por coma)</label>
                <Input value={f.opciones} onChange={(e) => setF({ ...f, opciones: e.target.value })} className="h-9" placeholder="Electrostática, Líquida, Otra" data-testid="vars-f-opciones" /></div>
            )}
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Familias donde aplica</label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="vars-f-familias">
                {familias.map((fam) => (
                  <button key={fam.v} type="button" onClick={() => toggleLista('familias', fam.v)}
                    className={`px-2 py-1 rounded-full text-xs font-semibold border ${f.familias.includes(fam.v) ? 'border-[#0EA5E9] text-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
                    {fam.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Depende de (código de variable)</label>
                <Input value={f.dep_var} onChange={(e) => setF({ ...f, dep_var: e.target.value })} className="h-9" placeholder="lleva_canales" data-testid="vars-f-depvar" /></div>
              <div><label className="text-xs font-bold text-[var(--text-muted)]">Valor requerido</label>
                <Input value={f.dep_val} onChange={(e) => setF({ ...f, dep_val: e.target.value })} className="h-9" placeholder="si" data-testid="vars-f-depval" /></div>
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Afecta a</label>
              <div className="flex flex-wrap gap-3 mt-1">
                {AFECTA.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={!!f.afecta[k]} onChange={(e) => setF({ ...f, afecta: { ...f.afecta, [k]: e.target.checked } })} className="accent-[#0EA5E9]" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Visible para (vacío = todos)</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ROLES_VIS.map(([k, label]) => (
                  <button key={k} type="button" onClick={() => toggleLista('visibilidad', k)}
                    className={`px-2 py-1 rounded-full text-xs font-semibold border ${f.visibilidad.includes(k) ? 'border-[#0EA5E9] text-[#0EA5E9] bg-[#0EA5E9]/10' : 'border-[var(--border)] text-[var(--text-muted)]'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} className="h-9" placeholder="Descripción" />
              <Input value={f.ayuda} onChange={(e) => setF({ ...f, ayuda: e.target.value })} className="h-9" placeholder="Texto de ayuda" />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={f.obligatoria} onChange={(e) => setF({ ...f, obligatoria: e.target.checked })} className="accent-[#0EA5E9]" /> Obligatoria
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={f.activa} onChange={(e) => setF({ ...f, activa: e.target.checked })} className="accent-[#0EA5E9]" data-testid="vars-f-activa" /> Activa
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={guardar} className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="vars-f-guardar">
                {editId ? 'Guardar cambios' : 'Crear variable'}
              </Button>
              <Button variant="outline" onClick={() => { setF(null); setEditId(null); }} data-testid="vars-f-cancelar">Cancelar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
